/**
 * Wrapper su `lightweight-charts` v5 (ADR-0009): la libreria è imperativa e
 * resta CONFINATA qui — il resto della slice vede solo props dichiarative.
 *
 * Regole di SPEC-016 rispettate dentro il wrapper:
 * - nessun colore hardcodato: tutti letti dai token CSS a runtime (ADR-0005);
 * - un dato assente non vale zero: i punti senza chiusura sono saltati, e i
 *   buchi dichiarati in `gaps` sono resi come tratteggio grigio SOLO visivo;
 * - in modalità linea la serie è colorata PER FONTE: un segmento per ogni
 *   tratto contiguo della stessa fonte.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CandlestickSeries,
  createChart,
  HistogramSeries,
  LineSeries,
  LineStyle,
  type IChartApi,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { SeriesGap, SeriesPoint } from './api';
import { splitBySource } from './split-by-source';

export type PriceChartMode = 'line' | 'candles';

type Props = {
  points: SeriesPoint[];
  gaps: SeriesGap[];
  granularity: 'daily' | 'intraday';
  mode: PriceChartMode;
  showVolume: boolean;
  logScale: boolean;
  /** Fonte → colore, condivisa con la legenda di DataProvenance. */
  sourceColors: Record<string, string>;
  height?: number;
};

type CrosshairInfo = {
  time: string;
  open: string | null;
  high: string | null;
  low: string | null;
  close: string | null;
  volume: string | null;
  source: string;
};

/** Token CSS letto a runtime: mai un colore scritto nel componente (ADR-0005). */
function cssToken(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function toChartTime(time: string, granularity: 'daily' | 'intraday'): Time {
  if (granularity === 'intraday') return Math.floor(Date.parse(time) / 1000) as UTCTimestamp;
  return time as Time; // 'YYYY-MM-DD' è un BusinessDay string accettato dalla libreria
}

export function PriceChart({
  points,
  gaps,
  granularity,
  mode,
  showVolume,
  logScale,
  sourceColors,
  height = 380,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const [crosshair, setCrosshair] = useState<CrosshairInfo | null>(null);

  // Solo i punti con una chiusura reale: un null non diventa mai uno zero.
  const drawable = useMemo(() => points.filter((point) => point.close !== null), [points]);
  const byTime = useMemo(() => new Map(drawable.map((point) => [point.time, point])), [drawable]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || drawable.length === 0) return;

    const text = cssToken('--color-text-muted');
    const border = cssToken('--color-border');
    const positive = cssToken('--color-positive');
    const negative = cssToken('--color-negative');
    const subtle = cssToken('--color-text-subtle');

    const chart = createChart(container, {
      height,
      autoSize: true,
      layout: { background: { color: 'transparent' }, textColor: text, attributionLogo: false },
      grid: {
        vertLines: { color: border, style: LineStyle.Dotted },
        horzLines: { color: border, style: LineStyle.Dotted },
      },
      rightPriceScale: { borderColor: border, mode: logScale ? 1 : 0 },
      timeScale: { borderColor: border },
      crosshair: { horzLine: { labelVisible: true } },
    });
    chartRef.current = chart;

    if (mode === 'candles') {
      const candles = chart.addSeries(CandlestickSeries, {
        upColor: positive,
        downColor: negative,
        borderUpColor: positive,
        borderDownColor: negative,
        wickUpColor: positive,
        wickDownColor: negative,
      });
      candles.setData(
        drawable
          .filter((point) => point.open && point.high && point.low)
          .map((point) => ({
            time: toChartTime(point.time, granularity),
            open: Number(point.open),
            high: Number(point.high),
            low: Number(point.low),
            close: Number(point.close),
          })),
      );
    } else {
      const runs = splitBySource(drawable);
      for (const run of runs) {
        const series = chart.addSeries(LineSeries, {
          color: sourceColors[run.source] ?? cssToken('--color-accent'),
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: run === runs.at(-1),
        });
        series.setData(
          run.points.map((point) => ({
            time: toChartTime(point.time, granularity),
            value: Number(point.close),
          })),
        );
      }
      // Buchi dichiarati: tratteggio grigio fra i due punti di confine.
      // Interpolazione SOLO visiva, mai salvata né spacciata per dato.
      for (const gap of gaps) {
        const before = [...drawable].reverse().find((point) => point.time < gap.from);
        const after = drawable.find((point) => point.time > gap.to);
        if (!before || !after) continue;
        const dashed = chart.addSeries(LineSeries, {
          color: subtle,
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        dashed.setData([
          { time: toChartTime(before.time, granularity), value: Number(before.close) },
          { time: toChartTime(after.time, granularity), value: Number(after.close) },
        ]);
      }
    }

    if (showVolume) {
      const volume = chart.addSeries(HistogramSeries, {
        priceScaleId: 'volume',
        priceFormat: { type: 'volume' },
        color: subtle,
      });
      chart.priceScale('volume').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
      volume.setData(
        drawable
          .filter((point) => point.volume !== null)
          .map((point) => {
            const up = point.open === null || Number(point.close) >= Number(point.open);
            return {
              time: toChartTime(point.time, granularity),
              value: Number(point.volume),
              color: up ? positive : negative,
            };
          }),
      );
    }

    chart.timeScale().fitContent();

    chart.subscribeCrosshairMove((param) => {
      if (!param.time) {
        setCrosshair(null);
        return;
      }
      const key =
        granularity === 'intraday'
          ? new Date((param.time as number) * 1000).toISOString()
          : (param.time as string);
      const point = byTime.get(key);
      setCrosshair(
        point
          ? {
              time: point.time,
              open: point.open,
              high: point.high,
              low: point.low,
              close: point.close,
              volume: point.volume,
              source: point.source,
            }
          : null,
      );
    });

    return () => {
      chartRef.current = null;
      chart.remove();
    };
  }, [drawable, byTime, gaps, granularity, mode, showVolume, logScale, sourceColors, height]);

  if (drawable.length === 0) {
    return (
      <div className="price-chart price-chart--empty" style={{ minHeight: height }}>
        <p>
          Nessun dato di prezzo in questo orizzonte. Usa «Scarica dati aggiuntivi» per popolare lo
          storico: il download passa dalla coda, con quota dichiarata.
        </p>
      </div>
    );
  }

  return (
    <div className="price-chart">
      <div ref={containerRef} className="price-chart__canvas" style={{ height }} data-testid="price-chart" />
      {crosshair && (
        <div className="price-chart__crosshair" role="status">
          <span className="numeric">{crosshair.time.length === 10 ? crosshair.time : crosshair.time.slice(0, 16).replace('T', ' ')}</span>
          {crosshair.open && <span className="numeric">A {crosshair.open}</span>}
          {crosshair.high && <span className="numeric">M {crosshair.high}</span>}
          {crosshair.low && <span className="numeric">m {crosshair.low}</span>}
          {crosshair.close && <span className="numeric">C {crosshair.close}</span>}
          {crosshair.volume && <span className="numeric">Vol {crosshair.volume}</span>}
          <span className="price-chart__crosshair-source">Fonte: {crosshair.source}</span>
        </div>
      )}
    </div>
  );
}
